import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { FileText, AlertTriangle, CheckCircle, Scale, Ban, CreditCard, Shield, Users, Gavel, Mail, Phone, MapPin } from "lucide-react";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-12 pt-28">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
            <FileText className="w-8 h-8 text-orange-500" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Terms of Service</h1>
          <p className="text-slate-600">Last Updated: April 29, 2026</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 space-y-8">
          
          {/* Important Notice */}
          <section className="bg-amber-50 p-6 rounded-lg border border-amber-200">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-amber-800 mb-2">Important Notice</h3>
                <p className="text-amber-700 text-sm leading-relaxed">
                  Please read these Terms of Service carefully before using ReBusi. By accessing or using our 
                  platform, you agree to be bound by these terms. If you disagree with any part of these terms, 
                  you may not access our services.
                </p>
              </div>
            </div>
          </section>

          {/* 1. Acceptance of Terms */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "you," 
              or "your") and ReBusi Technologies Pvt. Ltd. ("ReBusi," "we," "us," or "our"), governing your 
              access to and use of the ReBusi website, mobile application, and all related services (collectively, 
              the "Platform").
            </p>
            <p className="text-slate-700 leading-relaxed">
              By creating an account, accessing, or using our Platform, you acknowledge that you have read, 
              understood, and agree to be bound by these Terms, our Privacy Policy, and any additional terms 
              and conditions that may apply to specific features or services.
            </p>
          </section>

          {/* 2. Eligibility */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-orange-500" />
              2. Eligibility
            </h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              To use ReBusi, you must:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li>Be at least 18 years of age</li>
              <li>Have the legal capacity to enter into binding contracts</li>
              <li>Not be prohibited from using our services under applicable law</li>
              <li>Provide accurate, current, and complete registration information</li>
              <li>If registering on behalf of a business entity, have the authority to bind that entity to these Terms</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              We reserve the right to refuse service, terminate accounts, or remove content at our sole discretion.
            </p>
          </section>

          {/* 3. Account Registration */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Account Registration and Security</h2>
            <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">3.1 Account Creation</h3>
            <p className="text-slate-700 leading-relaxed mb-3">
              To access certain features, you must create an account. You agree to:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li>Provide accurate, truthful, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Keep your password confidential and secure</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Accept responsibility for all activities under your account</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">3.2 Account Types</h3>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li><strong>Buyer Account:</strong> For individuals or entities seeking to purchase businesses</li>
              <li><strong>Seller Account:</strong> For individuals or entities listing businesses for sale</li>
              <li><strong>Consultant Account:</strong> For verified business consultants offering services</li>
            </ul>
          </section>

          {/* 4. Platform Services */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Platform Services</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              ReBusi provides an online marketplace platform that facilitates:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li><strong>Business Listings:</strong> Sellers can list businesses for sale with detailed information</li>
              <li><strong>Business Discovery:</strong> Buyers can search and explore available businesses</li>
              <li><strong>Deal Facilitation:</strong> Structured deal tracking from initial interest to closing</li>
              <li><strong>Due Diligence Tools:</strong> Checklists and agreement templates</li>
              <li><strong>Consultation Services:</strong> Connect with verified business consultants</li>
              <li><strong>Franchise Opportunities:</strong> Explore franchise investment options</li>
              <li><strong>B2B Rentals:</strong> Equipment and space rental marketplace</li>
              <li><strong>AI-Powered Tools:</strong> Business valuation and review services</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4 font-medium">
              ReBusi acts as an intermediary platform and is not a party to any transaction between users.
            </p>
          </section>

          {/* 5. User Conduct */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Ban className="w-6 h-6 text-orange-500" />
              5. User Conduct and Prohibited Activities
            </h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              You agree NOT to engage in any of the following prohibited activities:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li>Providing false, misleading, or fraudulent information</li>
              <li>Creating fake listings or misrepresenting business details</li>
              <li>Circumventing the platform to conduct off-platform transactions</li>
              <li>Sharing contact information (phone, email) before appropriate deal stages</li>
              <li>Harassing, threatening, or intimidating other users</li>
              <li>Attempting to manipulate or game the platform's systems</li>
              <li>Scraping, data mining, or automated collection of information</li>
              <li>Uploading malicious code, viruses, or harmful content</li>
              <li>Impersonating another person or entity</li>
              <li>Violating any applicable laws or regulations</li>
              <li>Infringing on intellectual property rights</li>
              <li>Using the platform for money laundering or illegal financial activities</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              Violation of these rules may result in immediate account suspension or termination.
            </p>
          </section>

          {/* 6. Business Listings */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Business Listings and Transactions</h2>
            
            <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">6.1 Seller Responsibilities</h3>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li>Provide accurate and complete business information</li>
              <li>Disclose all material facts that may affect the transaction</li>
              <li>Ensure you have legal authority to sell the business</li>
              <li>Respond to inquiries in a timely and professional manner</li>
              <li>Honor commitments made during negotiations</li>
              <li>Provide required documents for due diligence</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">6.2 Buyer Responsibilities</h3>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li>Conduct thorough due diligence before committing</li>
              <li>Verify all claims and information independently</li>
              <li>Have adequate financial resources for the transaction</li>
              <li>Communicate honestly about your intentions and capabilities</li>
              <li>Respect confidentiality of business information</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">6.3 Transaction Process</h3>
            <p className="text-slate-700 leading-relaxed">
              All transactions follow our structured deal tracking system with defined stages. Both parties 
              must complete required steps and sign necessary agreements (NDA, Due Diligence Agreement, 
              Sale Agreement) through the platform.
            </p>
          </section>

          {/* 7. Fees and Payments */}
          <section className="bg-slate-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-orange-500" />
              7. Fees and Payments
            </h2>
            
            <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">7.1 Platform Fees</h3>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li><strong>Basic Listings:</strong> Free for standard business listings</li>
              <li><strong>Premium Features:</strong> Paid plans for featured listings and enhanced visibility</li>
              <li><strong>Success Fee:</strong> A percentage fee on completed transactions (as disclosed)</li>
              <li><strong>Consultation Bookings:</strong> Fees as displayed at time of booking</li>
              <li><strong>Buyer Requirements:</strong> Tiered pricing (Free, Premium, Concierge)</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">7.2 Payment Terms</h3>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li>All fees are in Indian Rupees (INR) unless otherwise specified</li>
              <li>Payments are processed through secure third-party providers</li>
              <li>Fees are non-refundable unless otherwise stated</li>
              <li>We reserve the right to modify fees with 30 days notice</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">7.3 Escrow Services</h3>
            <p className="text-slate-700 leading-relaxed">
              For qualifying transactions, escrow services may be available to protect both parties. 
              Escrow terms, fees, and release conditions will be specified in the transaction agreements.
            </p>
          </section>

          {/* 8. Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Intellectual Property</h2>
            
            <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">8.1 Platform Content</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              All content on ReBusi, including but not limited to text, graphics, logos, icons, images, 
              software, and the compilation thereof, is the property of ReBusi or its content suppliers 
              and is protected by Indian and international copyright laws.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">8.2 User Content</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              By posting content on ReBusi, you grant us a non-exclusive, worldwide, royalty-free license 
              to use, reproduce, modify, and display such content for the purpose of operating and promoting 
              the Platform. You retain ownership of your content.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">8.3 Trademarks</h3>
            <p className="text-slate-700 leading-relaxed">
              "ReBusi," our logo, and related marks are trademarks of ReBusi Technologies Pvt. Ltd. 
              You may not use these marks without our prior written permission.
            </p>
          </section>

          {/* 9. Disclaimers */}
          <section className="bg-red-50 p-6 rounded-lg border border-red-200">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              9. Disclaimers
            </h2>
            <div className="text-slate-700 leading-relaxed space-y-4">
              <p>
                <strong>9.1 Platform Role:</strong> ReBusi is a marketplace platform that connects buyers 
                and sellers. We do not own, operate, or control the businesses listed on our platform. 
                We are not a broker, agent, or advisor.
              </p>
              <p>
                <strong>9.2 No Guarantees:</strong> We do not guarantee the accuracy, completeness, or 
                reliability of any listing, user information, or content on the Platform. Users are 
                responsible for their own due diligence.
              </p>
              <p>
                <strong>9.3 As-Is Basis:</strong> THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" 
                WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO 
                IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
              </p>
              <p>
                <strong>9.4 AI Tools:</strong> AI-powered features (business valuation, reviews) provide 
                estimates for informational purposes only and should not be relied upon as professional 
                advice. Consult qualified professionals for official valuations and assessments.
              </p>
            </div>
          </section>

          {/* 10. Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Limitation of Liability</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li>ReBusi shall not be liable for any indirect, incidental, special, consequential, or 
                punitive damages arising from your use of the Platform</li>
              <li>Our total liability shall not exceed the fees paid by you to ReBusi in the 12 months 
                preceding the claim</li>
              <li>We are not liable for any losses resulting from transactions between users</li>
              <li>We are not responsible for the actions, content, or conduct of third parties</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              Some jurisdictions do not allow limitation of liability, so some of these limitations may 
              not apply to you.
            </p>
          </section>

          {/* 11. Indemnification */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-orange-500" />
              11. Indemnification
            </h2>
            <p className="text-slate-700 leading-relaxed">
              You agree to indemnify, defend, and hold harmless ReBusi, its officers, directors, employees, 
              agents, and affiliates from and against any claims, liabilities, damages, losses, costs, and 
              expenses (including reasonable legal fees) arising from:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4 mt-3">
              <li>Your use of the Platform</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Any content you post or submit</li>
              <li>Your transactions with other users</li>
            </ul>
          </section>

          {/* 12. Dispute Resolution */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Scale className="w-6 h-6 text-orange-500" />
              12. Dispute Resolution
            </h2>
            
            <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">12.1 Between Users</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Disputes between buyers and sellers should first be resolved through direct communication. 
              ReBusi may, at its discretion, provide mediation services but is not obligated to do so.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">12.2 With ReBusi</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Any dispute arising from these Terms shall be resolved as follows:
            </p>
            <ol className="list-decimal list-inside text-slate-700 space-y-2 ml-4">
              <li><strong>Negotiation:</strong> Attempt to resolve through good-faith negotiation</li>
              <li><strong>Mediation:</strong> If negotiation fails, submit to mediation in Mumbai</li>
              <li><strong>Arbitration:</strong> Binding arbitration under the Arbitration and Conciliation Act, 1996</li>
            </ol>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">12.3 Class Action Waiver</h3>
            <p className="text-slate-700 leading-relaxed">
              You agree to resolve disputes individually and waive any right to participate in class actions 
              or class-wide arbitration.
            </p>
          </section>

          {/* 13. Termination */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Termination</h2>
            
            <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">13.1 By You</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              You may terminate your account at any time by contacting us or using account settings. 
              Termination does not relieve you of obligations incurred prior to termination.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">13.2 By ReBusi</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We may suspend or terminate your account immediately, without prior notice, for:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li>Violation of these Terms</li>
              <li>Fraudulent or illegal activity</li>
              <li>Conduct harmful to other users or the Platform</li>
              <li>Extended period of inactivity</li>
              <li>At our sole discretion for any reason</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">13.3 Effect of Termination</h3>
            <p className="text-slate-700 leading-relaxed">
              Upon termination, your right to use the Platform ceases immediately. Provisions that by 
              their nature should survive termination shall survive (including indemnification, limitation 
              of liability, and dispute resolution).
            </p>
          </section>

          {/* 14. General Provisions */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">14. General Provisions</h2>
            
            <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-3">14.1 Governing Law</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              These Terms shall be governed by and construed in accordance with the laws of India. 
              The courts of Mumbai, Maharashtra shall have exclusive jurisdiction.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">14.2 Entire Agreement</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              These Terms, together with the Privacy Policy and any other legal notices, constitute the 
              entire agreement between you and ReBusi regarding the Platform.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">14.3 Severability</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If any provision of these Terms is found to be unenforceable, the remaining provisions 
              shall continue in full force and effect.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">14.4 No Waiver</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Our failure to enforce any right or provision shall not constitute a waiver of such right 
              or provision.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">14.5 Assignment</h3>
            <p className="text-slate-700 leading-relaxed">
              You may not assign or transfer these Terms without our prior written consent. We may assign 
              our rights and obligations without restriction.
            </p>
          </section>

          {/* 15. Changes to Terms */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">15. Changes to Terms</h2>
            <p className="text-slate-700 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify you of material changes by:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4 mt-3">
              <li>Posting the updated Terms on the Platform</li>
              <li>Updating the "Last Updated" date</li>
              <li>Sending an email notification for significant changes</li>
              <li>Displaying a prominent notice on the Platform</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              Continued use of the Platform after changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          {/* Contact Information */}
          <section className="bg-slate-100 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Mail className="w-6 h-6 text-orange-500" />
              16. Contact Information
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              For questions, concerns, or notices regarding these Terms of Service, please contact us:
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-slate-500" />
                <span className="text-slate-700">legal@rebusi.io</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-slate-500" />
                <span className="text-slate-700">+91 99999 99999</span>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-500 mt-1" />
                <span className="text-slate-700">
                  ReBusi Technologies Pvt. Ltd.<br />
                  123 Business Park, Sector 15<br />
                  Mumbai, Maharashtra 400001, India
                </span>
              </div>
            </div>
          </section>

          {/* Acknowledgment */}
          <section className="bg-emerald-50 p-6 rounded-lg border border-emerald-200">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-emerald-800 mb-2">Acknowledgment</h3>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  By using ReBusi, you acknowledge that you have read, understood, and agree to be bound 
                  by these Terms of Service and our Privacy Policy. If you do not agree to these terms, 
                  please do not use our Platform.
                </p>
              </div>
            </div>
          </section>

        </div>

        {/* Footer Note */}
        <p className="text-center text-slate-500 text-sm mt-8">
          These Terms of Service are effective as of April 29, 2026. 
          © 2026 ReBusi Technologies Pvt. Ltd. All rights reserved.
        </p>
      </div>

      <Footer />
    </div>
  );
}
